/**
 * ConnectYourAiSection - the one "Connect your AI" area on the Team page.
 * Three labelled rows: Relay Helper key, Personal connection (MCP URL),
 * Workspace connection (MCP URL). Every value is masked (prefix + last 4)
 * until the person clicks Reveal; Copy works without revealing.
 *
 * Workspace connection: GET /api/workspace/mcp-url (human_lead only — see
 * server.ts); any other caller gets a 403, rendered as an informational
 * callout. Personal key + URL: only known right after MyDaemonKeySection
 * creates one (TeamPage passes it in as `created`); the server never
 * returns it again, so otherwise the rows say so instead of showing a value.
 *
 * Row sentences are checked against server/mcp/teamLog.ts: `author` on
 * post_message is self-reported on every connection; only a personal key
 * carries `ownerUid` (the member), which is what the Lead-only tools
 * (relay_set_config, get_cloud_costs) and the Get started skip/resume tools
 * check. The workspace URL has no ownerUid, so Get started and relay_set_config
 * refuse it: on "default" it IS the legacy shared secret (relay_set_config
 * accepts only a Lead's personal key) and the other Lead-only tools fall back to the self-reported author name, and what logTeamMcpCall stamps as `owner_uid` on the audit log.
 */

import { useEffect, useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import { useActiveRoster } from "../../hooks";
import { CopyInstructionsButton } from "../CopyInstructionsButton";
import { buildDaemonKeyInstructions } from "../../lib/copyInstructions";

const URL_PATH = "/mcp/team-log/";
const KEY_PREFIX = "relay_wk_live_";

interface McpUrlResponse {
  mcp_url: string;
  status: "existing" | "generated" | "already_issued";
  error?: string;
}

/** Just-created personal key, from MyDaemonKeySection. Shown revealed once, as before. */
export interface CreatedKey {
  mcpUrl: string;
  status: "created" | "regenerated";
}

/** Prefix and last 4 characters, e.g. relay_wk_live_…MwN4. Never contains the middle of the secret. */
export function maskSecret(value: string): string {
  const keyAt = value.indexOf(KEY_PREFIX);
  const pathAt = value.indexOf(URL_PATH);
  const head = keyAt >= 0 ? value.slice(0, keyAt + KEY_PREFIX.length) : pathAt >= 0 ? value.slice(0, pathAt + URL_PATH.length) : "";
  return `${head}…${value.slice(-4)}`;
}

function keyFromUrl(url: string): string {
  return url.split(URL_PATH)[1] ?? "";
}

function ConnectionRow({
  label, description, value, unavailable, startRevealed,
}: {
  label: string;
  description: string;
  value: string;
  unavailable?: string;
  startRevealed?: boolean;
}) {
  const [revealed, setRevealed] = useState(!!startRevealed);
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="sc-item-row" style={{ display: "block", marginBottom: 12 }}>
      <div className="sc-item-name">{label}</div>
      <div style={{ fontSize: 13, color: "var(--muted)", fontFamily: "var(--font-ui)", margin: "2px 0 6px" }}>{description}</div>
      {value ? (
        <>
          <div
            data-testid="connection-value"
            style={{ fontFamily: "var(--font-mono)", fontSize: 13, wordBreak: "break-all", color: "var(--ink)", marginBottom: 6 }}
          >
            {revealed ? value : maskSecret(value)}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn sm" onClick={() => setRevealed((r) => !r)}>{revealed ? "Hide" : "Reveal"}</button>
            <button className="btn sm" onClick={handleCopy} style={{ color: copied ? "#0E9B6A" : undefined }}>
              {copied ? "✓ Copied!" : "Copy"}
            </button>
          </div>
        </>
      ) : (
        <div className="sc-item-meta">{unavailable}</div>
      )}
    </div>
  );
}

export function ConnectYourAiSection({ workspaceId = "default", created = null }: { workspaceId?: string; created?: CreatedKey | null }) {
  const [workspaceUrl, setWorkspaceUrl] = useState<string>("");
  const [status, setStatus] = useState<McpUrlResponse["status"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const roster = useActiveRoster(workspaceId);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const headers = await getAuthHeader(workspaceId);
        const res = await fetch("/api/workspace/mcp-url", { headers });
        const data = await res.json();
        if (cancelled) return;

        if (res.status === 403) {
          setForbidden(true);
          return;
        }
        if (!res.ok) {
          setError(data.error || `HTTP ${res.status}`);
          return;
        }

        setWorkspaceUrl(data.mcp_url ?? "");
        setStatus(data.status ?? null);
      } catch (err) {
        if (!cancelled) {
          console.error("[ConnectYourAiSection] Failed to load MCP URL:", err);
          setError("Network error. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [workspaceId]);

  const personalUnavailable = "Not shown again after it is created. Use “Create or regenerate my daemon key” below to get a new one.";
  let workspaceUnavailable = "";
  if (loading) workspaceUnavailable = "Loading…";
  else if (forbidden) workspaceUnavailable = "Only the workspace Lead can view the workspace connection. Ask your Lead to share it with you.";
  else if (error) workspaceUnavailable = error;
  else if (status === "already_issued") workspaceUnavailable = "Already issued for this workspace and only shown once, when it was generated.";

  // The created key replaces the row's React key so a new key starts revealed (once, as before).
  const createdKey = created ? keyFromUrl(created.mcpUrl) : "";
  const rowKey = created ? created.mcpUrl : "none";

  return (
    <section id="team-section-mcp" style={{ marginBottom: 32 }}>
      <div className="sc-section-label">Connect your AI</div>
      <div style={{ fontSize: 13, color: "var(--muted)", fontFamily: "var(--font-ui)", marginBottom: 12 }}>
        Three ways to connect an AI to this workspace. Each value is hidden until you click Reveal; Copy works without revealing.
      </div>

      {created && (
        <div className="sc-callout warn" style={{ marginBottom: 12 }}>
          <strong>Copy this now — it won't be shown again.</strong>{" "}
          {created.status === "regenerated"
            ? "Your previous key no longer works. Update every computer using it."
            : "This is your personal daemon key."}
        </div>
      )}

      <ConnectionRow
        key={`helper-${rowKey}`}
        label="Relay Helper key"
        description="The key alone, for Relay Helper (Settings → Daemon key) or relay install --secret. Relay Helper uses it to do work as you."
        value={createdKey}
        unavailable={personalUnavailable}
        startRevealed={!!created}
      />
      <ConnectionRow
        key={`personal-${rowKey}`}
        label="Personal connection (MCP URL)"
        description="Acts as you. An AI advisor connected with it reads and posts in the Log with your account recorded on every call, and can use the Lead-only tools if you are the Lead. The name on a post is the one the AI gives itself."
        value={created?.mcpUrl ?? ""}
        unavailable={personalUnavailable}
        startRevealed={!!created}
      />
      <ConnectionRow
        label="Workspace connection (MCP URL)"
        description="The shared workspace URL. An AI connected with it reads and posts in the Log, but it is not tied to anyone: no account is recorded, and Lead-only tools can't tell who is calling (relay_set_config and the Get started skip and resume refuse it, and other Lead-only tools go by the name the AI gives itself). Treat it as full workspace access. The name on a post is the one the AI gives itself."
        value={workspaceUrl}
        unavailable={workspaceUnavailable}
      />

      <div className="sc-callout warn" style={{ marginBottom: 12 }}>
        Treat these like passwords. Don't paste them into chats or screenshots.
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: "var(--muted)", fontFamily: "var(--font-ui)", marginBottom: 6 }}>
          In Claude.ai: <strong>Settings → Integrations → Add MCP Server</strong> → paste a connection URL.
        </div>
        {/* track:workspace-key-onboarding (decision #48253) — available up front, never contains a key
            or URL — see src/lib/copyInstructions.ts's own header comment. */}
        <CopyInstructionsButton text={buildDaemonKeyInstructions()} />
      </div>

      <div className="sc-item-row" style={{ display: "block" }}>
        <div className="sc-item-name" style={{ marginBottom: roster.length ? 6 : 0 }}>
          Connected agents
        </div>
        {roster.length === 0 ? (
          <div className="sc-item-meta">No agents connected yet.</div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            {roster.map((id) => (
              <span
                key={id}
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  padding: "2px 8px",
                  background: "var(--teal-tint, #EFF7F5)",
                  border: "1px solid var(--teal-mid)",
                  borderRadius: 999,
                  color: "var(--teal-dark)",
                }}
              >
                {id}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
